// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { SnackbarKey, useSnackbar } from 'notistack';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { ErrorContext } from '../contexts/ErrorContext';
import { CodeBlock } from './CodeBlock';

const ErrorWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const [errorStack, setErrorStack] = useState('');
  const [errorContext, setErrorContext] = useState<Error>();
  const [displayError, setDisplayError] = useState(false);

  const onDetails = useCallback(
    (key: SnackbarKey) => {
      setDisplayError(true);
      closeSnackbar(key);
    },
    [closeSnackbar],
  );

  const onClose = useCallback(
    (key: SnackbarKey) => {
      closeSnackbar(key);
    },
    [closeSnackbar],
  );

  const enqueueError = React.useCallback(
    (err: Error) => {
      setErrorContext(err);
      setErrorStack(err.stack || '');
      enqueueSnackbar(err.message, {
        variant: 'utilities',
        onUtilitiesDetails: onDetails,
        onUtilitiesClose: onClose,
        preventDuplicate: true,
        persist: true,
      });
    },
    [enqueueSnackbar, onClose, onDetails],
  );

  const Fallback: React.FC<FallbackProps> = useCallback(
    ({ error, resetErrorBoundary }) => {
      const errorT = error as Error;
      setErrorContext(errorT);
      enqueueSnackbar(errorT.message, {
        variant: 'utilities',
        onUtilitiesDetails: onDetails,
        onUtilitiesClose: onClose,
        onEnter: resetErrorBoundary,
        preventDuplicate: true,
        persist: true,
      });
      return <div />;
    },
    [enqueueSnackbar, onClose, onDetails],
  );
  const value = React.useMemo(
    () => ({
      enqueueError,
    }),
    [enqueueError],
  );
  return (
    <ErrorContext.Provider value={value}>
      <ErrorBoundary
        FallbackComponent={Fallback}
        onError={(error, info) => {
          setErrorContext(error);
          setErrorStack(info.componentStack || error.stack || '');
        }}
      >
        <Backdrop open={displayError}>
          <Dialog
            open={displayError}
            onClose={() => setDisplayError(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {errorContext?.message}
              <IconButton size="small" onClick={() => setDisplayError(false)}>
                <Close sx={{ color: 'text.primary' }} fontSize="medium" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Cause</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'grey.100' }}>
                  <CodeBlock>{JSON.stringify(errorContext?.cause)}</CodeBlock>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Stacktrace</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'grey.100' }}>
                  <CodeBlock>{errorStack}</CodeBlock>
                </AccordionDetails>
              </Accordion>
            </DialogContent>
          </Dialog>
        </Backdrop>
        {children}
      </ErrorBoundary>
    </ErrorContext.Provider>
  );
};

export default ErrorWrapper;
